/**
 * CredibilityGapsPanel - FDA Credibility Gaps Display Component
 * ============================================================================
 * DC-933: Standalone reusable component for displaying FDA AI Credibility
 * gaps with severity grouping, filtering, and export capabilities.
 *
 * UX Design Philosophy:
 * - Summary bar for quick status overview
 * - Severity-grouped cards (not table rows)
 * - Critical/Major gaps expanded by default (action-first)
 * - Progressive disclosure for minor/observation gaps
 * - Visual effort indicators (dots, not text)
 * - Easy export for audit documentation
 *
 * Pattern: Follows AlcoaFindingsPanel.tsx (DC-514)
 *
 * @version 1.0.0
 * @created 2026-01-10
 * ============================================================================
 */

import React, { useState, useMemo, useCallback } from 'react';
import './credibility-gaps-panel.css';

// ============================================================================
// Types
// ============================================================================

export type FindingSeverity = 'critical' | 'major' | 'minor' | 'observation';
export type EffortLevel = 'low' | 'medium' | 'high';

export type FDAGapCategory =
  | 'context_of_use'
  | 'representativeness'
  | 'sample_size'
  | 'accuracy'
  | 'completeness'
  | 'traceability'
  | 'documentation'
  | 'validation'
  | 'model_risk';

export interface FDACredibilityGap {
  id: string;
  category: FDAGapCategory;
  severity: FindingSeverity;
  fda_requirement: string;
  fda_reference?: string;
  title: string;
  description: string;
  impact: string;
  remediation: string;
  effort_level: EffortLevel;
  evidence_needed: string[];
}

export interface CredibilityGapsPanelProps {
  /** Array of gaps to display */
  gaps: FDACredibilityGap[];
  /** Callback for export functionality */
  onExport?: (format: 'csv' | 'json') => void;
  /** Default severity filter */
  defaultFilter?: FindingSeverity | 'all';
  /** Severities to auto-expand */
  autoExpandSeverity?: FindingSeverity[];
  /** Maximum height for scrollable container */
  maxHeight?: string;
  /** Show summary bar at top */
  showSummaryBar?: boolean;
  /** Show filter chips */
  showFilters?: boolean;
  /** Custom className */
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

const SEVERITY_CONFIG: Record<FindingSeverity, {
  label: string;
  color: string;
  bgColor: string;
  icon: string;
  priority: number;
}> = {
  critical: {
    label: 'Critical',
    color: '#DC2626',
    bgColor: '#FEF2F2',
    icon: '⛔',
    priority: 1,
  },
  major: {
    label: 'Major',
    color: '#EA580C',
    bgColor: '#FFF7ED',
    icon: '⚠',
    priority: 2,
  },
  minor: {
    label: 'Minor',
    color: '#CA8A04',
    bgColor: '#FEFCE8',
    icon: '!',
    priority: 3,
  },
  observation: {
    label: 'Observation',
    color: '#2563EB',
    bgColor: '#EFF6FF',
    icon: 'ℹ',
    priority: 4,
  },
};

const CATEGORY_LABELS: Record<FDAGapCategory, string> = {
  context_of_use: 'Context of Use (Step 1)',
  representativeness: 'Representativeness (Step 2)',
  sample_size: 'Sample Size (Step 3)',
  accuracy: 'Accuracy (Step 4)',
  completeness: 'Completeness (Step 5)',
  traceability: 'Traceability (Step 6)',
  documentation: 'Documentation',
  validation: 'Validation',
  model_risk: 'Model Risk',
};

const EFFORT_DOTS: Record<EffortLevel, number> = {
  low: 1,
  medium: 2,
  high: 3,
};

// ============================================================================
// Helper Components
// ============================================================================

interface EffortIndicatorProps {
  level: EffortLevel;
}

const EffortIndicator: React.FC<EffortIndicatorProps> = ({ level }) => {
  const filled = EFFORT_DOTS[level];

  return (
    <div className="cgp-effort" title={`${level.charAt(0).toUpperCase() + level.slice(1)} effort`}>
      <span className="cgp-effort__label">Effort:</span>
      <span className="cgp-effort__dots">
        {[1, 2, 3].map((dot) => (
          <span
            key={dot}
            className={`cgp-effort__dot ${dot <= filled ? 'cgp-effort__dot--filled' : ''}`}
          />
        ))}
      </span>
      <span className="cgp-effort__text">{level}</span>
    </div>
  );
};

interface GapCardProps {
  gap: FDACredibilityGap;
  expanded: boolean;
  onToggle: () => void;
}

const GapCard: React.FC<GapCardProps> = ({ gap, expanded, onToggle }) => {
  const severityConfig = SEVERITY_CONFIG[gap.severity];
  const categoryLabel = CATEGORY_LABELS[gap.category] || gap.category;

  return (
    <div
      className={`cgp-gap-card cgp-gap-card--${gap.severity}`}
      style={{
        borderLeftColor: severityConfig.color,
      }}
    >
      <button
        className="cgp-gap-card__header"
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <div className="cgp-gap-card__title-row">
          <span
            className="cgp-gap-card__severity-icon"
            style={{ color: severityConfig.color }}
          >
            {severityConfig.icon}
          </span>
          <span className="cgp-gap-card__title">{gap.title}</span>
          <svg
            className={`cgp-gap-card__chevron ${expanded ? 'cgp-gap-card__chevron--expanded' : ''}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
        <div className="cgp-gap-card__meta">
          <span className="cgp-gap-card__category">{categoryLabel}</span>
          <span className="cgp-gap-card__separator">·</span>
          <span className="cgp-gap-card__requirement">{gap.fda_requirement}</span>
        </div>
      </button>

      {!expanded && (
        <div className="cgp-gap-card__description-preview">
          {gap.description}
        </div>
      )}

      {expanded && (
        <div className="cgp-gap-card__details">
          <div className="cgp-gap-card__section">
            <h5 className="cgp-gap-card__section-title">Description</h5>
            <p className="cgp-gap-card__section-content">{gap.description}</p>
          </div>

          <div className="cgp-gap-card__section">
            <h5 className="cgp-gap-card__section-title">Impact</h5>
            <p className="cgp-gap-card__section-content">{gap.impact}</p>
          </div>

          <div className="cgp-gap-card__remediation">
            <h5 className="cgp-gap-card__section-title">Remediation</h5>
            <p className="cgp-gap-card__section-content">{gap.remediation}</p>
            <div className="cgp-gap-card__remediation-meta">
              <EffortIndicator level={gap.effort_level} />
              {gap.evidence_needed.length > 0 && (
                <div className="cgp-gap-card__evidence">
                  <span className="cgp-gap-card__evidence-label">Evidence needed:</span>
                  <div className="cgp-gap-card__evidence-tags">
                    {gap.evidence_needed.map((evidence) => (
                      <span key={evidence} className="cgp-gap-card__evidence-tag">
                        {evidence.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface SeverityGroupProps {
  severity: FindingSeverity;
  gaps: FDACredibilityGap[];
  expandedIds: Set<string>;
  onToggle: (id: string) => void;
  defaultExpanded: boolean;
}

const SeverityGroup: React.FC<SeverityGroupProps> = ({
  severity,
  gaps,
  expandedIds,
  onToggle,
  defaultExpanded,
}) => {
  const [groupExpanded, setGroupExpanded] = useState(defaultExpanded);
  const severityConfig = SEVERITY_CONFIG[severity];

  if (gaps.length === 0) return null;

  return (
    <div className="cgp-severity-group">
      <button
        className="cgp-severity-group__header"
        onClick={() => setGroupExpanded(!groupExpanded)}
        style={{
          backgroundColor: severityConfig.bgColor,
          borderColor: severityConfig.color,
        }}
      >
        <span
          className="cgp-severity-group__label"
          style={{ color: severityConfig.color }}
        >
          {severityConfig.label.toUpperCase()} ({gaps.length})
        </span>
        <svg
          className={`cgp-severity-group__chevron ${groupExpanded ? 'cgp-severity-group__chevron--expanded' : ''}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {groupExpanded && (
        <div className="cgp-severity-group__content">
          {gaps.map((gap) => (
            <GapCard
              key={gap.id}
              gap={gap}
              expanded={expandedIds.has(gap.id)}
              onToggle={() => onToggle(gap.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const CredibilityGapsPanel: React.FC<CredibilityGapsPanelProps> = ({
  gaps,
  onExport,
  defaultFilter = 'all',
  autoExpandSeverity = ['critical', 'major'],
  maxHeight,
  showSummaryBar = true,
  showFilters = true,
  className = '',
}) => {
  const [activeFilter, setActiveFilter] = useState<FindingSeverity | 'all'>(defaultFilter);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => {
    // Auto-expand critical and major gaps by default
    const initialExpanded = new Set<string>();
    gaps.forEach((gap) => {
      if (autoExpandSeverity.includes(gap.severity)) {
        initialExpanded.add(gap.id);
      }
    });
    return initialExpanded;
  });
  const [exportMenuOpen, setExportMenuOpen] = useState(false);

  // Compute summary counts
  const summary = useMemo(() => {
    const counts = {
      critical: 0,
      major: 0,
      minor: 0,
      observation: 0,
      total: gaps.length,
    };
    gaps.forEach((gap) => {
      counts[gap.severity]++;
    });
    return counts;
  }, [gaps]);

  // Filter gaps
  const filteredGaps = useMemo(() => {
    if (activeFilter === 'all') return gaps;
    return gaps.filter((gap) => gap.severity === activeFilter);
  }, [gaps, activeFilter]);

  // Group gaps by severity
  const groupedGaps = useMemo(() => {
    const groups: Record<FindingSeverity, FDACredibilityGap[]> = {
      critical: [],
      major: [],
      minor: [],
      observation: [],
    };
    filteredGaps.forEach((gap) => {
      groups[gap.severity].push(gap);
    });
    return groups;
  }, [filteredGaps]);

  const handleToggleGap = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleExport = useCallback((format: 'csv' | 'json') => {
    setExportMenuOpen(false);
    if (onExport) {
      onExport(format);
    } else {
      // Default export implementation
      exportGapsDefault(gaps, format);
    }
  }, [gaps, onExport]);

  // Empty state
  if (gaps.length === 0) {
    return (
      <div className={`cgp-panel cgp-panel--empty ${className}`}>
        <div className="cgp-empty-state">
          <svg
            className="cgp-empty-state__icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="cgp-empty-state__text">No credibility gaps identified</p>
          <p className="cgp-empty-state__subtext">
            The data meets FDA credibility requirements
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`cgp-panel ${className}`}>
      {/* Header with Summary and Export */}
      <div className="cgp-panel__header">
        <h3 className="cgp-panel__title">Credibility Gaps</h3>
        {onExport && (
          <div className="cgp-export-menu">
            <button
              className="cgp-export-menu__trigger"
              onClick={() => setExportMenuOpen(!exportMenuOpen)}
              aria-expanded={exportMenuOpen}
            >
              Export
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {exportMenuOpen && (
              <div className="cgp-export-menu__dropdown">
                <button onClick={() => handleExport('csv')}>Export CSV</button>
                <button onClick={() => handleExport('json')}>Export JSON</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Summary Bar */}
      {showSummaryBar && (
        <div className="cgp-summary-bar">
          <span className="cgp-summary-bar__total">{summary.total} total gaps</span>
          <span className="cgp-summary-bar__separator">·</span>
          {summary.critical > 0 && (
            <span
              className="cgp-summary-bar__count"
              style={{ color: SEVERITY_CONFIG.critical.color }}
            >
              {summary.critical} critical
            </span>
          )}
          {summary.major > 0 && (
            <span
              className="cgp-summary-bar__count"
              style={{ color: SEVERITY_CONFIG.major.color }}
            >
              {summary.major} major
            </span>
          )}
          {summary.minor > 0 && (
            <span
              className="cgp-summary-bar__count"
              style={{ color: SEVERITY_CONFIG.minor.color }}
            >
              {summary.minor} minor
            </span>
          )}
          {summary.observation > 0 && (
            <span
              className="cgp-summary-bar__count"
              style={{ color: SEVERITY_CONFIG.observation.color }}
            >
              {summary.observation} obs
            </span>
          )}
        </div>
      )}

      {/* Filter Chips */}
      {showFilters && (
        <div className="cgp-filters" role="group" aria-label="Filter by severity">
          <button
            className={`cgp-filter-chip ${activeFilter === 'all' ? 'cgp-filter-chip--active' : ''}`}
            onClick={() => setActiveFilter('all')}
          >
            All ({summary.total})
          </button>
          {(['critical', 'major', 'minor', 'observation'] as FindingSeverity[]).map((sev) => (
            <button
              key={sev}
              className={`cgp-filter-chip ${activeFilter === sev ? 'cgp-filter-chip--active' : ''}`}
              onClick={() => setActiveFilter(sev)}
              style={{
                '--chip-color': SEVERITY_CONFIG[sev].color,
                '--chip-bg': SEVERITY_CONFIG[sev].bgColor,
              } as React.CSSProperties}
              disabled={summary[sev] === 0}
            >
              {SEVERITY_CONFIG[sev].label} ({summary[sev]})
            </button>
          ))}
        </div>
      )}

      {/* Gaps List */}
      <div
        className="cgp-gaps-list"
        style={maxHeight ? { maxHeight, overflowY: 'auto' } : undefined}
      >
        {(['critical', 'major', 'minor', 'observation'] as FindingSeverity[]).map((severity) => (
          <SeverityGroup
            key={severity}
            severity={severity}
            gaps={groupedGaps[severity]}
            expandedIds={expandedIds}
            onToggle={handleToggleGap}
            defaultExpanded={autoExpandSeverity.includes(severity)}
          />
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// Default Export Function
// ============================================================================

function exportGapsDefault(gaps: FDACredibilityGap[], format: 'csv' | 'json'): void {
  let content: string;
  let mimeType: string;
  let filename: string;

  if (format === 'json') {
    content = JSON.stringify(gaps, null, 2);
    mimeType = 'application/json';
    filename = `fda-credibility-gaps-${new Date().toISOString().split('T')[0]}.json`;
  } else {
    // CSV format
    const headers = [
      'ID',
      'Severity',
      'Category',
      'FDA Requirement',
      'Title',
      'Description',
      'Impact',
      'Remediation',
      'Effort Level',
      'Evidence Needed',
    ];
    const rows = gaps.map((gap) => [
      gap.id,
      gap.severity,
      CATEGORY_LABELS[gap.category] || gap.category,
      gap.fda_requirement,
      `"${gap.title.replace(/"/g, '""')}"`,
      `"${gap.description.replace(/"/g, '""')}"`,
      `"${gap.impact.replace(/"/g, '""')}"`,
      `"${gap.remediation.replace(/"/g, '""')}"`,
      gap.effort_level,
      `"${gap.evidence_needed.join(', ')}"`,
    ]);
    content = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
    mimeType = 'text/csv';
    filename = `fda-credibility-gaps-${new Date().toISOString().split('T')[0]}.csv`;
  }

  // Create and download blob
  const blob = new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

export default CredibilityGapsPanel;
